/**
 * TQL Autocomplete - Provide context-aware suggestions for query editing
 */

import type { TqlSuggestion, FilterPreset } from './types';
import { TQL_FIELDS, OPERATOR_LABELS } from './types';

export type AutocompleteContext = {
  presets: FilterPreset[];
  recentValues?: {
    hosts?: string[];
    paths?: string[];
    methods?: string[];
    statusCodes?: number[];
  };
};

// Common status codes for autocomplete
const COMMON_STATUS_CODES = [
  { code: 200, label: '200 OK' },
  { code: 201, label: '201 Created' },
  { code: 204, label: '204 No Content' },
  { code: 301, label: '301 Moved Permanently' },
  { code: 302, label: '302 Found' },
  { code: 304, label: '304 Not Modified' },
  { code: 400, label: '400 Bad Request' },
  { code: 401, label: '401 Unauthorized' },
  { code: 403, label: '403 Forbidden' },
  { code: 404, label: '404 Not Found' },
  { code: 405, label: '405 Method Not Allowed' },
  { code: 422, label: '422 Unprocessable Entity' },
  { code: 429, label: '429 Too Many Requests' },
  { code: 500, label: '500 Internal Server Error' },
  { code: 502, label: '502 Bad Gateway' },
  { code: 503, label: '503 Service Unavailable' },
  { code: 504, label: '504 Gateway Timeout' },
];

/**
 * Analyze the query context to determine what kind of suggestions to provide
 */
type QueryContext = {
  type:
    | 'empty'
    | 'namespace'
    | 'field'
    | 'operator'
    | 'value'
    | 'after_expression'
    | 'preset';
  namespace?: string;
  field?: string;
  operator?: string;
  partial?: string;
};

function analyzeQueryContext(text: string): QueryContext {
  const trimmed = text.trim();

  // Empty or whitespace
  if (!trimmed) {
    return { type: 'empty' };
  }

  // After logical operator - suggest new expression
  if (/\b(AND|OR)\s*$/i.test(trimmed)) {
    return { type: 'empty' };
  }

  // After NOT - suggest new expression
  if (/\bNOT\s*$/i.test(trimmed)) {
    return { type: 'empty' };
  }

  // After open paren - suggest new expression
  if (/\(\s*$/.test(trimmed)) {
    return { type: 'empty' };
  }

  // After closing paren - suggest logical operators
  if (/\)\s*$/.test(trimmed)) {
    return { type: 'after_expression' };
  }

  // Typing preset reference
  if (/preset:\s*$/.test(trimmed)) {
    return { type: 'preset', partial: '' };
  }
  const presetMatch = trimmed.match(/preset:(\S*)$/);
  if (presetMatch) {
    return { type: 'preset', partial: presetMatch[1] };
  }

  // Inside value (after operator with open paren)
  const valueMatch = trimmed.match(
    /((?:req|resp)\.[a-z]+|\b(?:comment|tag|highlight|id|timestamp))\.(\w+)\(([^)]*)$/i,
  );
  if (valueMatch) {
    return {
      type: 'value',
      field: valueMatch[1].toLowerCase(),
      operator: valueMatch[2].toLowerCase(),
      partial: valueMatch[3].replace(/^["']/, ''),
    };
  }

  // After operator name (before open paren)
  const afterOpMatch = trimmed.match(
    /((?:req|resp)\.[a-z]+|\b(?:comment|tag|highlight|id|timestamp))\.(\w+)$/i,
  );
  if (afterOpMatch) {
    const fieldName = afterOpMatch[1].toLowerCase();
    const possibleOp = afterOpMatch[2].toLowerCase();
    const fieldMeta = TQL_FIELDS.find((f) => f.field === fieldName);

    if (fieldMeta) {
      // Check if this is a complete operator or partial
      const isCompleteOp = fieldMeta.operators.includes(possibleOp as never);
      if (isCompleteOp) {
        // Operator is complete, suggest opening paren or show usage
        return {
          type: 'operator',
          field: fieldName,
          operator: possibleOp,
          partial: '',
        };
      } else {
        // Partial operator
        return {
          type: 'operator',
          field: fieldName,
          partial: possibleOp,
        };
      }
    }
  }

  // After field with dot - suggest operators
  const fieldDotMatch = trimmed.match(
    /((?:req|resp)\.[a-z]+|\b(?:comment|tag|highlight|id|timestamp))\.$/i,
  );
  if (fieldDotMatch) {
    return {
      type: 'operator',
      field: fieldDotMatch[1].toLowerCase(),
      partial: '',
    };
  }

  // Typing namespace.field (e.g., "req.pa")
  const nsFieldMatch = trimmed.match(/(req|resp)\.([a-z]*)$/i);
  if (nsFieldMatch) {
    return {
      type: 'field',
      namespace: nsFieldMatch[1].toLowerCase(),
      partial: nsFieldMatch[2].toLowerCase(),
    };
  }

  // Typing namespace only (e.g., "req" or "re")
  const nsMatch = trimmed.match(/\b(req?|res?|pre?s?e?t?)$/i);
  if (nsMatch) {
    return {
      type: 'namespace',
      partial: nsMatch[1].toLowerCase(),
    };
  }

  // Standalone field being typed
  const standaloneMatch = trimmed.match(
    /\b(comm?e?n?t?|tag?|high?l?i?g?h?t?|id|time?s?t?a?m?p?)$/i,
  );
  if (standaloneMatch) {
    return {
      type: 'field',
      partial: standaloneMatch[1].toLowerCase(),
    };
  }

  // Default - typing something new
  const lastToken = trimmed.match(/[\w.-]*$/)?.[0] || '';
  return {
    type: 'namespace',
    partial: lastToken,
  };
}

/**
 * Get autocomplete suggestions based on current input and cursor position
 */
export function getTqlSuggestions(
  input: string,
  cursorPosition: number,
  context: AutocompleteContext,
): TqlSuggestion[] {
  const textBeforeCursor = input.substring(0, cursorPosition);
  const queryContext = analyzeQueryContext(textBeforeCursor);

  switch (queryContext.type) {
    case 'empty':
      return [
        ...getNamespaceSuggestions(''),
        ...getStandaloneFieldSuggestions(''),
        ...getPresetSuggestions(context.presets, ''),
        {
          type: 'keyword',
          label: 'NOT',
          insertText: 'NOT ',
          description: 'Negate expression',
        },
        {
          type: 'keyword',
          label: '(',
          insertText: '(',
          description: 'Group expressions',
        },
      ];

    case 'namespace':
      return [
        ...getNamespaceSuggestions(queryContext.partial || ''),
        ...getStandaloneFieldSuggestions(queryContext.partial || ''),
        ...getPresetSuggestions(context.presets, queryContext.partial || ''),
      ];

    case 'field':
      if (queryContext.namespace) {
        return getFieldsForNamespace(
          queryContext.namespace,
          queryContext.partial || '',
        );
      }
      return [
        ...getFieldSuggestions(queryContext.partial || ''),
        ...getStandaloneFieldSuggestions(queryContext.partial || ''),
      ];

    case 'operator':
      return getOperatorSuggestionsForField(
        queryContext.field || '',
        queryContext.partial || '',
      );

    case 'value':
      return getValueSuggestions(
        queryContext.field || '',
        queryContext.operator || '',
        queryContext.partial || '',
        context,
      );

    case 'after_expression':
      return [
        {
          type: 'keyword',
          label: 'AND',
          insertText: ' AND ',
          description: 'Both must match',
        },
        {
          type: 'keyword',
          label: 'OR',
          insertText: ' OR ',
          description: 'Either must match',
        },
      ];

    case 'preset':
      return getPresetSuggestions(context.presets, queryContext.partial || '');

    default:
      return [];
  }
}

function getNamespaceSuggestions(partial: string): TqlSuggestion[] {
  const namespaces = [
    {
      ns: 'req',
      label: 'Request',
      description: 'Request fields (method, path, host, body, headers)',
    },
    {
      ns: 'resp',
      label: 'Response',
      description: 'Response fields (code, body, roundtrip, headers)',
    },
  ];

  return namespaces
    .filter((n) => n.ns.startsWith(partial.toLowerCase()))
    .map((n) => ({
      type: 'field' as const,
      label: n.ns,
      insertText: n.ns + '.',
      description: n.label,
      detail: n.description,
    }));
}

function getStandaloneFieldSuggestions(partial: string): TqlSuggestion[] {
  const standaloneFields = ['comment', 'tag', 'highlight', 'id', 'timestamp'];
  const lower = partial.toLowerCase();

  return standaloneFields
    .filter((f) => f.startsWith(lower))
    .map((f) => {
      const fieldMeta = TQL_FIELDS.find((m) => m.field === f);
      return {
        type: 'field' as const,
        label: f,
        insertText: f + '.',
        description: fieldMeta?.label || f,
        detail: fieldMeta?.description,
      };
    });
}

function getFieldsForNamespace(
  namespace: string,
  partial: string,
): TqlSuggestion[] {
  const prefix = namespace + '.';
  const lower = partial.toLowerCase();

  return TQL_FIELDS.filter((f) => f.field.startsWith(prefix))
    .filter((f) => {
      const fieldPart = f.field.substring(prefix.length);
      return fieldPart.startsWith(lower);
    })
    .map((f) => ({
      type: 'field' as const,
      label: f.field,
      insertText: f.field + '.',
      description: f.label,
      detail: f.description,
    }));
}

function getFieldSuggestions(partial: string): TqlSuggestion[] {
  const lower = partial.toLowerCase();
  return TQL_FIELDS.filter(
    (field) =>
      field.field.toLowerCase().startsWith(lower) ||
      field.label.toLowerCase().includes(lower),
  ).map((field) => ({
    type: 'field' as const,
    label: field.field,
    insertText: field.field + '.',
    description: field.label,
    detail: field.description,
  }));
}

function getOperatorSuggestionsForField(
  fieldName: string,
  partial: string,
): TqlSuggestion[] {
  const fieldMeta = TQL_FIELDS.find((f) => f.field === fieldName);
  if (!fieldMeta) {
    // Fallback to all operators
    return Object.entries(OPERATOR_LABELS)
      .filter(([op]) => op.toLowerCase().startsWith(partial.toLowerCase()))
      .map(([op, meta]) => ({
        type: 'operator' as const,
        label: op,
        insertText: op === 'exists' || op === 'nexists' ? `${op}()` : `${op}(`,
        description: meta.description,
      }));
  }

  const lower = partial.toLowerCase();
  return fieldMeta.operators
    .filter((op) => op.toLowerCase().startsWith(lower))
    .map((op) => {
      const meta = OPERATOR_LABELS[op as keyof typeof OPERATOR_LABELS];
      const needsValue = op !== 'exists' && op !== 'nexists';
      return {
        type: 'operator' as const,
        label: op,
        insertText: needsValue ? `${op}(` : `${op}()`,
        description: meta?.description || op,
        detail: needsValue
          ? fieldMeta.valueType === 'number'
            ? 'number'
            : '"string"'
          : undefined,
      };
    });
}

function getValueSuggestions(
  fieldName: string,
  operator: string,
  partial: string,
  context: AutocompleteContext,
): TqlSuggestion[] {
  const suggestions: TqlSuggestion[] = [];
  const fieldMeta = TQL_FIELDS.find((f) => f.field === fieldName);
  const lower = partial.toLowerCase();
  const isNumeric = fieldMeta?.valueType === 'number';

  // For status codes
  if (fieldName === 'resp.code') {
    for (const status of COMMON_STATUS_CODES) {
      if (
        String(status.code).startsWith(partial) ||
        status.label.toLowerCase().includes(lower)
      ) {
        suggestions.push({
          type: 'value',
          label: String(status.code),
          insertText: `${status.code})`,
          description: status.label,
        });
      }
    }
    return suggestions;
  }

  // For response length
  if (fieldName === 'resp.length') {
    const sizes = [
      { value: 0, label: '0 (empty)' },
      { value: 1000, label: '1KB' },
      { value: 10000, label: '10KB' },
      { value: 100000, label: '100KB' },
      { value: 1000000, label: '1MB' },
    ];
    for (const size of sizes) {
      if (String(size.value).startsWith(partial)) {
        suggestions.push({
          type: 'value',
          label: String(size.value),
          insertText: `${size.value})`,
          description: size.label,
        });
      }
    }
    return suggestions;
  }

  // For methods
  if (fieldName === 'req.method') {
    const methods = context.recentValues?.methods || [
      'GET',
      'POST',
      'PUT',
      'DELETE',
      'PATCH',
      'OPTIONS',
      'HEAD',
    ];
    for (const method of methods) {
      if (method.toLowerCase().startsWith(lower)) {
        suggestions.push({
          type: 'value',
          label: method,
          insertText: `"${method}")`,
          description: 'HTTP method',
        });
      }
    }
    return suggestions;
  }

  // For hosts - suggest recent hosts
  if (fieldName === 'req.host') {
    const hosts = context.recentValues?.hosts || [];
    for (const host of hosts.slice(0, 10)) {
      if (host.toLowerCase().includes(lower)) {
        suggestions.push({
          type: 'value',
          label: host,
          insertText: `"${host}")`,
          description: 'Recent host',
        });
      }
    }
    // Also add example
    if (!hosts.length || 'api.example.com'.includes(lower)) {
      suggestions.push({
        type: 'value',
        label: 'api.example.com',
        insertText: '"api.example.com")',
        description: 'Example host',
      });
    }
    return suggestions;
  }

  // For extensions
  if (fieldName === 'req.ext') {
    const extensions = [
      '.js',
      '.css',
      '.html',
      '.json',
      '.png',
      '.jpg',
      '.gif',
      '.svg',
      '.woff',
      '.woff2',
      '.ico',
      '.xml',
    ];
    for (const ext of extensions) {
      if (ext.toLowerCase().includes(lower)) {
        suggestions.push({
          type: 'value',
          label: ext,
          insertText: `"${ext}")`,
          description: 'File extension',
        });
      }
    }
    return suggestions;
  }

  // For highlight colors
  if (fieldName === 'highlight') {
    const colors = [
      'red',
      'orange',
      'yellow',
      'green',
      'blue',
      'purple',
      'gray',
    ];
    for (const color of colors) {
      if (color.includes(lower)) {
        suggestions.push({
          type: 'value',
          label: color,
          insertText: `"${color}")`,
          description: 'Highlight color',
        });
      }
    }
    return suggestions;
  }

  // For MIME types
  if (fieldName === 'resp.mime') {
    const mimeTypes = [
      'application/json',
      'text/html',
      'text/javascript',
      'text/css',
      'image/png',
      'image/jpeg',
      'image/svg+xml',
      'application/xml',
      'application/octet-stream',
    ];
    for (const mime of mimeTypes) {
      if (mime.toLowerCase().includes(lower)) {
        suggestions.push({
          type: 'value',
          label: mime,
          insertText: `"${mime}")`,
          description: 'MIME type',
        });
      }
    }
    return suggestions;
  }

  // For paths - suggest common patterns
  if (fieldName === 'req.path') {
    const patterns = [
      '/api',
      '/api/',
      '/login',
      '/auth',
      '/admin',
      '/static',
      '/assets',
    ];
    for (const pattern of patterns) {
      if (pattern.includes(lower)) {
        suggestions.push({
          type: 'value',
          label: pattern,
          insertText: `"${pattern}")`,
          description: 'Common path pattern',
        });
      }
    }
    return suggestions;
  }

  // Add field-specific examples as fallback
  if (fieldMeta?.examples) {
    for (const example of fieldMeta.examples) {
      if (example.toLowerCase().includes(lower)) {
        suggestions.push({
          type: 'value',
          label: example,
          insertText: isNumeric ? `${example})` : `"${example}")`,
          description: 'Example value',
        });
      }
    }
  }

  return suggestions;
}

function getPresetSuggestions(
  presets: FilterPreset[],
  partial: string,
): TqlSuggestion[] {
  const lower = partial.toLowerCase();
  return presets
    .filter(
      (preset) =>
        preset.alias.toLowerCase().startsWith(lower) ||
        preset.name.toLowerCase().includes(lower),
    )
    .map((preset) => ({
      type: 'preset' as const,
      label: `preset:${preset.alias}`,
      insertText: `preset:${preset.alias}`,
      description: preset.name,
      detail: preset.description,
    }));
}

/**
 * Tokenize an TQL query for syntax highlighting
 */
export type HighlightToken = {
  text: string;
  type:
    | 'namespace'
    | 'field'
    | 'operator'
    | 'value'
    | 'keyword'
    | 'preset'
    | 'paren'
    | 'text'
    | 'error';
  start: number;
  end: number;
};

export function tokenizeForHighlight(query: string): HighlightToken[] {
  const tokens: HighlightToken[] = [];
  let pos = 0;

  const patterns: Array<{ regex: RegExp; type: HighlightToken['type'] }> = [
    // Keywords (AND, OR, NOT)
    { regex: /^(AND|OR|NOT)\b/i, type: 'keyword' },
    // Preset reference
    { regex: /^preset:[a-zA-Z0-9_-]+/, type: 'preset' },
    // Namespace + field + operator + value pattern
    { regex: /^(req|resp)\.[a-z]+\.[a-z]+\([^)]*\)/i, type: 'text' },
    // Namespace.field
    { regex: /^(req|resp)\.[a-z]+/i, type: 'field' },
    // Standalone fields
    { regex: /^(comment|tag|highlight|id|timestamp)\b/i, type: 'field' },
    // Operators
    {
      regex:
        /^\.(eq|neq|ne|cont|ncont|starts|ends|regex|gt|gte|lt|lte|exists|nexists)\b/i,
      type: 'operator',
    },
    // String values
    { regex: /^"(?:[^"\\]|\\.)*"/, type: 'value' },
    { regex: /^'(?:[^'\\]|\\.)*'/, type: 'value' },
    // Numbers
    { regex: /^\d+/, type: 'value' },
    // Parentheses
    { regex: /^[()]/, type: 'paren' },
    // Dots (part of syntax)
    { regex: /^\./, type: 'text' },
    // Whitespace
    { regex: /^\s+/, type: 'text' },
  ];

  while (pos < query.length) {
    let matched = false;

    for (const { regex, type } of patterns) {
      const match = query.substring(pos).match(regex);
      if (match) {
        const text = match[0];

        // Special handling for compound patterns
        if (type === 'text' && text.includes('.') && text.includes('(')) {
          // This is a compound like req.method.eq("GET")
          // Split into parts
          const compoundTokens = tokenizeCompound(text, pos);
          tokens.push(...compoundTokens);
        } else {
          tokens.push({
            text,
            type,
            start: pos,
            end: pos + text.length,
          });
        }

        pos += text.length;
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Unknown character - add as text
      tokens.push({
        text: query[pos],
        type: 'text',
        start: pos,
        end: pos + 1,
      });
      pos++;
    }
  }

  return tokens;
}

function tokenizeCompound(text: string, startPos: number): HighlightToken[] {
  const tokens: HighlightToken[] = [];
  let pos = 0;

  // Match: namespace.field.operator(value)
  const match = text.match(/^((?:req|resp)\.[a-z]+)\.([a-z]+)\(([^)]*)\)$/i);
  if (match) {
    const [, field, operator, value] = match;

    // Field
    tokens.push({
      text: field,
      type: 'field',
      start: startPos + pos,
      end: startPos + pos + field.length,
    });
    pos += field.length;

    // Dot
    tokens.push({
      text: '.',
      type: 'text',
      start: startPos + pos,
      end: startPos + pos + 1,
    });
    pos += 1;

    // Operator
    tokens.push({
      text: operator,
      type: 'operator',
      start: startPos + pos,
      end: startPos + pos + operator.length,
    });
    pos += operator.length;

    // Open paren
    tokens.push({
      text: '(',
      type: 'paren',
      start: startPos + pos,
      end: startPos + pos + 1,
    });
    pos += 1;

    // Value (if any)
    if (value) {
      tokens.push({
        text: value,
        type: 'value',
        start: startPos + pos,
        end: startPos + pos + value.length,
      });
      pos += value.length;
    }

    // Close paren
    tokens.push({
      text: ')',
      type: 'paren',
      start: startPos + pos,
      end: startPos + pos + 1,
    });

    return tokens;
  }

  // Fallback: just return as text
  return [
    {
      text,
      type: 'text',
      start: startPos,
      end: startPos + text.length,
    },
  ];
}

/**
 * Get documentation for the TQL syntax
 */
export function getTqlDocumentation(): string {
  return `
# TQL - Terra Query Language

## Namespaces
- \`req\` - Request properties
- \`resp\` - Response properties
- \`preset\` - Saved filter presets

## Request Fields
- \`req.method\` - HTTP method (GET, POST, etc.)
- \`req.path\` - URL path (without query)
- \`req.query\` - Query string parameters
- \`req.host\` - Host/domain
- \`req.port\` - Port number
- \`req.body\` - Request body
- \`req.raw\` - Raw request (headers + body)
- \`req.ext\` - File extension
- \`req.header["Name"]\` - Request header value

## Response Fields
- \`resp.code\` - Status code (200, 404, etc.)
- \`resp.body\` - Response body
- \`resp.raw\` - Raw response (headers + body)
- \`resp.roundtrip\` - Response time (ms)
- \`resp.length\` - Response size (bytes)
- \`resp.mime\` - Content-Type
- \`resp.header["Name"]\` - Response header value

## Metadata
- \`comment\` - Annotation comment
- \`tag\` - Tags
- \`highlight\` - Highlight color

## String Operators
- \`eq("value")\` - Equals
- \`neq("value")\` - Not equals
- \`cont("value")\` - Contains
- \`ncont("value")\` - Not contains
- \`starts("value")\` - Starts with
- \`ends("value")\` - Ends with
- \`regex("pattern")\` - Regex match

## Numeric Operators
- \`eq(100)\` - Equals
- \`gt(100)\` - Greater than
- \`gte(100)\` - Greater than or equal
- \`lt(100)\` - Less than
- \`lte(100)\` - Less than or equal

## Existence Operators
- \`exists()\` - Has a value
- \`nexists()\` - Is empty

## Logical Operators
- \`AND\` - Both must match
- \`OR\` - Either must match
- \`NOT\` - Negate
- \`()\` - Grouping

## Examples
\`\`\`
req.path.cont("/api")
req.method.eq("POST") AND resp.code.gte(400)
NOT req.ext.eq(".js")
req.header["Authorization"].exists()
resp.roundtrip.gt(1000)
preset:hide-static AND req.host.cont("example.com")
\`\`\`
`.trim();
}
